import { useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import Prestation from "./prestation";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  addSelectedPrestation,
  clearSelectedPrestations,
} from "../store/slices/rendezVousSlice";
import { setSelectedEtablissement } from "../store/slices/etablissementSlice";
import CustomSelect from "./inputs/select";

const EtablissementServices = ({
  services,
  horaires,
  disponibilites = [],
  status,
  etablissement,
}) => {
  const [selectedService, setSelectedService] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [how, setHow] = useState(null);
  const [filteredPrestations, setFilteredPrestations] = useState(null);
  const sliderRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize default selected service
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const serviceId = params.get("service");

    if (services?.length > 0) {
      let serviceToSelect;

      if (serviceId) {
        serviceToSelect = services.find((s) => s.id === parseInt(serviceId));
      }

      if (!serviceToSelect) {
        serviceToSelect = services[0];
      }

      setSelectedService(serviceToSelect);
      if (how) applyFilter(serviceToSelect, how);
    }
  }, [services, location.search, how]);

  // Update scroll states
  useEffect(() => {
    const updateScrollStates = () => {
      if (sliderRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
        setSliderPosition(scrollLeft);
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };

    const el = sliderRef.current;
    if (!el) return;

    updateScrollStates();
    el.addEventListener("scroll", updateScrollStates, { passive: true });
    window.addEventListener("resize", updateScrollStates);

    return () => {
      el.removeEventListener("scroll", updateScrollStates);
      window.removeEventListener("resize", updateScrollStates);
    };
  }, []);

  const applyFilter = (service, filterValue) => {
    if (!filterValue) {
      setFilteredPrestations(null);
      return;
    }

    const filtered = service.prestations.filter((prestation) => {
      if (filterValue === "") return true;
      return prestation.lieu?.includes(filterValue);
    });
    setFilteredPrestations(filtered);
  };

  const handleServiceChange = (service) => {
    setSelectedService(service);
    navigate(`?service=${service.id}`, {
      state: { idEtablissement: location?.state?.idEtablissement },
    });
    if (how) {
      applyFilter(service, how);
    } else {
      setFilteredPrestations(null);
    }
  };

  const filterData = (value) => {
    setHow(value);
    if (selectedService) {
      applyFilter(selectedService, value);
    }
  };

  // Carousel scroll with item-based navigation
  const handleScroll = (direction) => {
    console.log(sliderRef);
    if (!sliderRef.current) return;

    const container = sliderRef.current;
    const children = Array.from(container.children);
    const containerWidth = container.offsetWidth;
    const currentScroll = container.scrollLeft;

    let targetIndex = -1;

    if (direction === "next") {
      // Find first item that's not fully visible
      for (let i = 0; i < children.length; i++) {
        const item = children[i];
        const itemRight = item.offsetLeft + item.offsetWidth;
        const visibleRight = currentScroll + containerWidth;

        if (itemRight > visibleRight + 1) {
          targetIndex = i;
          break;
        }
      }
    } else {
      // Find last item that's not fully visible to the left
      for (let i = children.length - 1; i >= 0; i--) {
        const item = children[i];
        const itemLeft = item.offsetLeft;

        if (itemLeft < currentScroll - 1) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex !== -1) {
      const targetItem = children[targetIndex];
      let newPosition;

      if (direction === "next") {
        // Align item to left edge
        newPosition = targetItem.offsetLeft;
      } else {
        // Align item to right edge or left if at start
        newPosition = Math.max(
          0,
          targetItem.offsetLeft - containerWidth + targetItem.offsetWidth
        );
      }

      container.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
    }
  };

  // Auto-scroll to make selected service visible
  useEffect(() => {
    if (selectedService && sliderRef.current) {
      const container = sliderRef.current;
      const serviceElements = Array.from(container.children);
      const selectedElement = serviceElements.find(
        (el) => el.textContent === selectedService.name
      );

      if (selectedElement) {
        const elementLeft = selectedElement.offsetLeft;
        const elementRight = elementLeft + selectedElement.offsetWidth;
        const containerLeft = container.scrollLeft;
        const containerRight = containerLeft + container.offsetWidth;

        if (elementLeft < containerLeft) {
          // Element is to the left of visible area
          container.scrollTo({
            left: elementLeft - 12,
            behavior: "smooth",
          });
        } else if (elementRight > containerRight) {
          // Element is to the right of visible area
          container.scrollTo({
            left: elementRight - container.offsetWidth + 12,
            behavior: "smooth",
          });
        }
      }
    }
  }, [selectedService]);

  // Loading state

  if (!services || services.length === 0) {
    return (
      <div className="etablissement-services">Aucun service disponible.</div>
    );
  }
  if (status === "loading") {
    return (
      <div className="etablissement-services">
        <div className="single row">
          <div className="services-col col-md-12">
            <h3 className="grey500">
              <Skeleton width={200} />
            </h3>
            <div className="service-cards">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="service-card">
                  <Skeleton width={150} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="etablissement-services">
        <div className="single row">
          <div className="services-col col-md-12">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="grey500 mb-0">Réservez votre service</h3>
              </div>
              <div className="col-auto">
                <CustomSelect
                  options={[
                    { label: "Tous", value: "" },
                    { label: "À domicile", value: "onsite" },
                    { label: "En ligne", value: "enligne" },
                    { label: "Chez le professionnel", value: "chezpro" },
                  ]}
                  placeholder="Filtrer"
                  onChange={filterData}
                  selectedValue={how}
                  className="filterbtn"
                  icon={
                    <img
                      src="/images/filter.svg"
                      alt="Trier"
                      className="mr-8"
                    />
                  }
                />
              </div>
            </div>

            {/* Carousel Container */}
            <div className="service-carousel-container d-flex align-items-center gap-2">
              {/* Services Carousel */}
              <div
                className="service-cards-carousel"
                ref={sliderRef}
                style={{
                  display: "flex",
                  gap: "12px",
                  overflowX: "auto",
                  padding: "6px 4px",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                {services.map((service) => (
                  <div
                    key={service.id}
                    className={`service-card-carousel ${
                      selectedService?.id === service.id ? "active" : ""
                    }`}
                    onClick={() => handleServiceChange(service)}
                    style={{
                      flex: "0 0 auto",
                      padding: "8px 18px",
                      borderRadius: "9999px",
                      whiteSpace: "nowrap",
                      border:
                        selectedService?.id === service.id
                          ? "none"
                          : "1px solid #e5e7eb",
                      background:
                        selectedService?.id === service.id ? "#2563eb" : "#fff",
                      color:
                        selectedService?.id === service.id ? "#fff" : "#374151",
                      fontWeight: 600,
                      cursor: "pointer",
                      position: "relative",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {service.name}
                  </div>
                ))}
              </div>
              {/* Left Arrow */}
              <button
                onClick={() => handleScroll("prev")}
                className="carousel-arrow link-blue"
                // disabled={!canScrollLeft}
                aria-label="Services précédents"
              >
                <img src="/images/left.svg" alt="Previous" />
              </button>
              {/* Right Arrow */}
              <button
                onClick={() => handleScroll("next")}
                className="carousel-arrow link-blue"
                // disabled={!canScrollRight}
                aria-label="Services suivants"
              >
                <img
                  src="/images/left.svg"
                  alt="Next"
                  style={{ transform: "rotate(180deg)" }}
                />
              </button>
            </div>

            {/* CSS for carousel */}
            <style>{`
            .service-cards-carousel::-webkit-scrollbar { display: none; }
            .service-cards-carousel.is-dragging { cursor: grabbing; user-select: none; }
            .service-card-carousel:hover {
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
          `}</style>

            {selectedService && (
              <div className="prestations mt-4">
                {selectedService.prestations?.length === 0 ? (
                  <p className="body grey66">
                    Aucune prestation disponible pour ce service.
                  </p>
                ) : (
                  <>
                    {(filteredPrestations || selectedService.prestations)
                      ?.slice()
                      ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                      ?.slice(0, showAll ? undefined : 5)
                      .map((prestation, index) => (
                        <div key={index} className="mb-3">
                          <Prestation
                            prestation={prestation}
                            classNameBtn="twyny-cta-secondary"
                            buttonName="Réserver"
                            buttonAction={() => {
                              navigate(`/details-rdv/${etablissement.id}`);
                              dispatch(clearSelectedPrestations());
                              dispatch(addSelectedPrestation(prestation));
                              dispatch(setSelectedEtablissement(etablissement));
                            }}
                          />
                        </div>
                      ))}
                    {selectedService.prestations?.length > 5 && (
                      <div className="text-center mt-3">
                        <button
                          className="link-blue"
                          onClick={() => setShowAll(!showAll)}
                        >
                          {showAll
                            ? "Voir moins"
                            : "Voir toutes les prestations"}
                          <br />
                          <img
                            src="/images/down-arrow.svg"
                            alt="down arrow icon"
                          />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default EtablissementServices;
